/**
 * Integration State Management
 *
 * Pure Jotai atoms for managing integration state.
 * Follows IVE's strict separation: atoms only, no business logic.
 */

import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type {
  IntegrationStatus,
  IntegrationManifest,
} from "@pineseed/gui/types/connection";

// Configuration atom with persistence
export const integrationConfigAtom =
  atomWithStorage<IntegrationManifest | null>("ive:integration-config", null);

// Runtime status atoms
export const integrationStatusAtom = atom<Record<string, IntegrationStatus>>(
  {},
);

export const integrationErrorsAtom = atom<
  Array<{
    id: string;
    error: Error;
    context: string;
    timestamp: number;
  }>
>([]);

export const integrationInitializedAtom = atom(false);

// ===== oRPC CONNECTION STATUS AND RETRY MANAGEMENT =====

export interface ORPCConnectionStatus {
  connected: boolean;
  retryCount: number;
  lastError: Error | null;
  lastAttempt: number | null;
  nextRetryAt: number | null;
  isRetrying: boolean;
}

export const orpcConnectionStatusAtom = atom<ORPCConnectionStatus>({
  connected: false,
  retryCount: 0,
  lastError: null,
  lastAttempt: null,
  nextRetryAt: null,
  isRetrying: true,
});

// Retry configuration - from environment variables only
export const orpcRetryConfigAtom = atom(async () => {
  // Dynamic import to avoid circular dependencies
  const { envConfig } = await import("../config/env.js");
  return {
    retryInterval: envConfig.integrations.orpc.retryInterval,
    connectionTimeout: envConfig.integrations.orpc.connectionTimeout,
    maxRetryAttempts: envConfig.integrations.orpc.maxRetryAttempts,
    healthCheckInterval: envConfig.integrations.orpc.healthCheckInterval,
  };
});

// Derived atoms
export const hasActiveIntegrationsAtom = atom((get) => {
  const status = get(integrationStatusAtom);
  return Object.values(status).some((s) => s.connected);
});

export const integrationErrorCountAtom = atom((get) => {
  const errors = get(integrationErrorsAtom);
  return errors.length;
});

export const natsIntegrationStatusAtom = atom((get) => {
  const status = get(integrationStatusAtom);
  return status.nats || { connected: false, errorCount: 0 };
});

// ===== oRPC CONNECTION ACTIONS =====

export const updateORPCConnectionStatusAtom = atom(
  null,
  (get, set, update: Partial<ORPCConnectionStatus>) => {
    const current = get(orpcConnectionStatusAtom);
    set(orpcConnectionStatusAtom, { ...current, ...update });
  },
);

export const recordORPCConnectionErrorAtom = atom(
  null,
  async (get, set, error: Error) => {
    const current = get(orpcConnectionStatusAtom);
    const retryConfig = await get(orpcRetryConfigAtom);

    // Use environment-configured retry interval
    const delay = retryConfig.retryInterval;

    set(orpcConnectionStatusAtom, {
      ...current,
      connected: false,
      lastError: error,
      lastAttempt: Date.now(),
      nextRetryAt: Date.now() + delay,
      retryCount: current.retryCount + 1,
      isRetrying: false,
    });
  },
);

export const resetORPCConnectionStatusAtom = atom(null, (_get, set) => {
  set(orpcConnectionStatusAtom, {
    connected: true,
    retryCount: 0,
    lastError: null,
    lastAttempt: Date.now(),
    nextRetryAt: null,
    isRetrying: false,
  });
});

export const setORPCRetryingStatusAtom = atom(
  null,
  (get, set, isRetrying: boolean) => {
    const current = get(orpcConnectionStatusAtom);
    set(orpcConnectionStatusAtom, { ...current, isRetrying });
  },
);

// Action atoms for integration management
export const initializeIntegrationsAtom = atom(null, async (get, set) => {
  try {
    // Dynamic import to respect boundaries
    const { integrationManager } = await import(
      "../integration/integration-manager.js"
    );

    const callbacks = {
      onStateUpdate: (update: Record<string, unknown>) => {
        // Handle state updates if needed
        console.log("Integration state update:", update);
      },
      onError: (error: Error, context: string) => {
        const errors = get(integrationErrorsAtom);
        set(integrationErrorsAtom, [
          ...errors,
          {
            id: crypto.randomUUID(),
            error,
            context,
            timestamp: Date.now(),
          },
        ]);
      },
      onStatusChange: (status: IntegrationStatus) => {
        const currentStatus = get(integrationStatusAtom);
        set(integrationStatusAtom, {
          ...currentStatus,
          nats: status, // Assuming NATS for now, could be more dynamic
        });
      },
    };

    await integrationManager.initialize(callbacks);
    set(integrationInitializedAtom, true);
  } catch (error) {
    console.error("Failed to initialize integrations:", error);
  }
});

export const updateIntegrationConfigAtom = atom(
  null,
  async (get, set, config: Partial<IntegrationManifest>) => {
    try {
      const { integrationManager } = await import(
        "../integration/integration-manager.js"
      );
      await integrationManager.updateConfig(config);

      const currentConfig = get(integrationConfigAtom);
      if (currentConfig) {
        const newConfig = { ...currentConfig, ...config };
        set(integrationConfigAtom, newConfig);
      }
    } catch (error) {
      console.error("Failed to update integration config:", error);
    }
  },
);

export const flushIntegrationsAtom = atom(null, async (_get, _set) => {
  try {
    const { integrationManager } = await import(
      "../integration/integration-manager.js"
    );
    await integrationManager.flushAllIntegrations();
  } catch (error) {
    console.error("Failed to flush integrations:", error);
  }
});

export const clearIntegrationErrorsAtom = atom(null, (_get, set) => {
  set(integrationErrorsAtom, []);
});
